import React from 'react';
import useCareLabels from 'hooks/useCareLabels';
import CareLabelSkeleton from 'components/common/CareLabelSkeleton/CareLabelSkeleton';
import { HiOutlineEye, HiTrash, HiPencil } from "react-icons/hi";
import TableHeader from "../../../../components/common/Table/TableHeader";
import CircularLoader from "../../../../components/common/CircularLoader/CircularLoader";
import AvatarGroup from "../../../../components/common/Avatar/AvatarGroup";
import { FEEDBACK_TABLE_HEADERS } from "../../../../store/constants";
import moment from 'moment';
import { capitalizeWords }  from "Utils/utils";

const getStatusColorClass = (status) => {
  const statusLower = status?.toLowerCase();
  if (statusLower === 'completed' || statusLower === 'complete') {
    return 'bg-green-100 text-green-800 border border-green-200';
  }
  if (statusLower === 'pending') {
    return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
  }
  if (statusLower === 'in_progress') {
    return 'bg-blue-100 text-blue-800 border border-blue-200';
  }
  return 'bg-gray-100 text-gray-800 border border-gray-200';
};

const FeedbackTable = ({
  loading,
  feedbacks,
  sortBy,
  sortDescending,
  flipSort,
  onView,
  onEdit,
  onDelete
}) => {
  const { participantLabelPlural, isLoading: isCareLabelsLoading } = useCareLabels();
  const resolveHeaderLabel = (label) => {
    if (label === "Participant(s)") {
      return isCareLabelsLoading ? (
        <CareLabelSkeleton width="w-28" height="h-3.5" />
      ) : (
        participantLabelPlural
      );
    }
    return label;
  };

  return (
      <div className='w-full mx-auto flex flex-col shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
    {/* <div className='table-wrp shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'> */}
      <table className='min-w-full divide-y divide-gray-300'>
        <thead className='bg-blue-50 pt-2 sticky top-[8px] capitalize  z-10'>
          <tr>
            {FEEDBACK_TABLE_HEADERS?.map((header, index) => (
              header.sortable ? (
                <TableHeader
                  key={index}
                  sortName={header.sortName}
                  flipSort={flipSort}
                  sortBy={sortBy}
                  sortDescending={sortDescending}
                  label={resolveHeaderLabel(header.label)}
                />
              ) : (
                <th
                  key={index}
                  scope='col'
                  className={`px-3  normal-case py-3.5 ${header.center ? 'text-center' : 'text-left'} text-sm font-semibold text-gray-900`}
                >
                  {resolveHeaderLabel(header.label)}
                </th>
              )
            ))}
          </tr>
        </thead>

        <tbody className='divide-y divide-gray-200 bg-white'>
          {loading ? (
            <tr>
              <td colSpan={9} className='bg-gray-50 py-2'>
                <CircularLoader
                  classes='flex justify-center items-center'
                  classloader='loader-l'
                />
              </td>
            </tr>
          ) : feedbacks?.data?.length > 0 ? (
            feedbacks?.data?.map((item, index) => (
              <FeedbackTableRow
                key={index}
                item={item}
                index={index}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
                participantLabelPlural={participantLabelPlural}
                isCareLabelsLoading={isCareLabelsLoading}
              />
            ))
          ) : (
            <tr>
              <td colSpan={9} className='py-6 text-center text-red-600'>
                No data found !
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const FeedbackTableRow = ({ item, index, onView, onEdit, onDelete, participantLabelPlural, isCareLabelsLoading }) => (
  <tr key={index} className='even:bg-white odd:bg-gray-50'>
    <td
    //   title={item?.uuid ? item?.uuid?.substring(0, 8).toUpperCase() : 'N/A'}
      className='whitespace-nowrap py-4 px-3 text-sm font-medium text-gray-900'
    >
      <div className='text-sm font-medium text-blue-600'>
        {item?.uuid ? item?.uuid?.substring(0, 8).toUpperCase() : 'N/A'}
      </div>
    </td>

   <td className='truncate w-[20px] px-3 py-4 text-sm' title={item?.subject}>
      {item?.subject || 'N/A'}

   </td>
   <td className='truncate px-3 py-4 text-sm'>
      {item?.created_by?.name || 'N/A'}

   </td>
    <td className='whitespace-nowrap px-3 py-4 text-sm'>
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColorClass(item?.status)}`}>
        {item?.status?.toLowerCase() === 'in_progress'
          ? 'In Progress'
          : item?.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'N/A'}
      </span>
    </td>

      <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
        {moment(item?.date).format("DD/MM/YYYY") || 'N/A'}
    </td>

    <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
      <div className='flex items-center'>
        {item?.participants && item?.participants.length > 0 ? (
          item?.participants.length === 1 ? (
            <>
              <span className='truncate whitespace-nowrap overflow-ellipsis block'>
                {capitalizeWords(item?.participants[0]?.name) || 'N/A'}
              </span>
            </>
          ) : (
            <AvatarGroup
              arrayData={item?.participants?.map(participant => ({
                ...participant,
                first_name: participant?.name?.split(' ')[0] || '',
                last_name: participant?.name?.split(' ').slice(1).join(' ') || ''
              }))}
              cssClass='mt-0'
              maxLength={3}
              isNoRing={true}
            />
          )
        ) : (
          <span className='text-gray-400'>
            No{" "}
            {isCareLabelsLoading ? (
              <CareLabelSkeleton width="w-20" height="h-3.5" className="bg-gray-300 align-middle" />
            ) : (
              participantLabelPlural.toLowerCase()
            )}
          </span>
        )}
      </div>
    </td>

    <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
       <div className='flex items-center'>
        {item?.mention_managers && item?.mention_managers.length > 0 ? (
          item?.mention_managers.length === 1 ? (
            <>
              <span className='truncate whitespace-nowrap overflow-ellipsis block'>
                {item?.mention_managers[0]?.name || 'N/A'}
              </span>
            </>
          ) : (
            <AvatarGroup
              arrayData={item?.mention_managers?.map(manager => ({
                ...manager,
                first_name: manager?.name?.split(' ')[0] || '',
                last_name: manager?.name?.split(' ').slice(1).join(' ') || ''
              }))}
              cssClass='mt-0'
              maxLength={3}
              isNoRing={true}
            />
          )
        ) : (
          <span className='text-gray-400'>No managers</span>
        )}
      </div>
    </td>

    <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
      <div className='flex items-center'>
        {item?.workers && item?.workers.length > 0 ? (
          item?.workers.length === 1 ? (
            <>
              <span className='truncate whitespace-nowrap overflow-ellipsis block'>
                {item?.workers[0]?.name || 'N/A'}
              </span>
            </>
          ) : (
            <AvatarGroup
              arrayData={item?.workers?.map(worker => ({
                ...worker,
                first_name: worker?.name?.split(' ')[0] || '',
                last_name: worker?.name?.split(' ').slice(1).join(' ') || ''
              }))}
              cssClass='mt-0'
              maxLength={3}
              isNoRing={true}
            />
          )
        ) : (
          <span className='text-gray-400'>No workers</span>
        )}
      </div>
    </td>

    <td className='whitespace-nowrap text-sm text-center text-gray-500'>
      <div className="flex items-center justify-center gap-1 ">
        <button onClick={() => onView(item)}>
          <div className='icon-eye-wrapper' title='View'>
            <HiOutlineEye className='icon-size' />
          </div>
        </button>
        {onEdit && (
          <button onClick={() => onEdit(item)}>
            <div className='icon-pencil-wrapper' title='Edit'>
              <HiPencil className='icon-size' />
            </div>
          </button>
        )}
        <button onClick={() => onDelete(item)}>
          <div className='icon-delete-wrapper' title='Delete'>
            <HiTrash className='icon-size' />
          </div>
        </button>
      </div>
    </td>
  </tr>
);

export default FeedbackTable;
